import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { QueryKeys } from "@/queries/utils";
import {
  AdminPipelineCandidate,
  AdminPipelineSummary,
} from "@/queries/admin/candidate-pipeline";
import { CandidateWaitingRoomStatus } from "@/types";
import { AdminSummary } from "@/queries/admin/dashboard";
import { CandidateApprovalFormSchema } from "@/components/forms/candidate/update-candidate-status/schema";

interface SetCandidateStageParams {
  candidateId: string;
  stage: CandidateWaitingRoomStatus;
  approvalData?: CandidateApprovalFormSchema;
}

export const useSetCandidateStage = (
  onSuccess?: (data: AdminPipelineCandidate) => void,
) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({
      candidateId,
      stage,
      approvalData,
    }: SetCandidateStageParams) => {
      const token = await getAccessTokenSilently();

      return request<AdminPipelineCandidate>({
        method: "PATCH",
        path: `/api/admin/candidate/${candidateId}/stage`,
        token,
        body: JSON.stringify({ stage: stage, ...approvalData }),
      });
    },
    onSuccess: (data) => {
      // Check if the candidate reached a final stage and should be dropped from the pipeline
      const candidateDone =
        data.stage === "Joined" ||
        data.stage === "Rejected" ||
        data.stage === "Dropped out";

      let candidateWasCounted = false;

      qc.setQueryData<{
        candidates: AdminPipelineCandidate[];
        summary: AdminPipelineSummary;
      }>(QueryKeys.adminCandidatePipeline, (prev) => {
        if (!prev) {
          return prev;
        }

        const candidates = [...prev.candidates];
        const candidateIndex = candidates.findIndex(
          (candidate) => candidate.candidateId === data.candidateId,
        );

        // If the element is found
        if (candidateIndex !== -1 && candidates[candidateIndex]) {
          // Check if the candidate has been moved from a stage taht requires Venturous action
          candidateWasCounted = [
            "Application Under Review",
            "Interview Under Review",
            "Ready For MAP",
            "MAP Under Review",
          ].includes(candidates[candidateIndex].stage);

          // Remove it if the candidate reached a final stage
          if (candidateDone) {
            candidates.splice(candidateIndex, 1);
          } else {
            // Update it otherwise
            candidates[candidateIndex] = data;
          }

          return {
            ...prev,
            candidates,
          };
        }
      });

      const candidateIsCounted = [
        "Application Under Review",
        "Interview Under Review",
        "Ready For MAP",
        "MAP Under Review",
      ].includes(data.stage);

      // If the candidate has moved from a counted to uncounted stage, or vice versa, update openApplicationsCount
      if (candidateWasCounted !== candidateIsCounted) {
        qc.setQueryData<AdminSummary>(QueryKeys.adminDashboard, (old) => {
          if (!old) return;

          // If the candidate no longer counts, reduce by 1, otherwise increment by 1
          const delta = candidateWasCounted ? -1 : 1;

          return {
            ...old,
            openApplicationsCount: old.openApplicationsCount + delta,
          };
        });
      }

      onSuccess?.(data);
    },
  });

  return {
    setCandidateStage: mutate,
    isSettingCandidateStage: isPending,
    setCandidateStageError: error,
  };
};
